import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import AlterationRowWithPhoto from './AlterationRowWithPhoto';
import {
  ALTERATION_ZONES,
  EDEMA_OPTIONS,
  GAIT_TYPE_OPTIONS,
  PLANTAR_PRINT_OPTIONS,
  SENSITIVITY_ROWS,
  TEMPERATURE_OPTIONS,
} from '../../lib/clinicalIntakeSchema';

function RadioOption({ name, value, current, label, readOnly, onSelect }) {
  const id = `${name}-${value}`;
  return (
    <label htmlFor={id} className="flex items-center gap-2 cursor-pointer py-1">
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={current === value}
        disabled={readOnly}
        onChange={() => onSelect(value)}
        className="h-4 w-4 accent-primary"
      />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function SectionTitle({ children }) {
  return <h4 className="text-sm font-semibold text-foreground mt-2">{children}</h4>;
}

export default function VascularExplorationSection({
  value,
  readOnly,
  recordId,
  onChange,
}) {
  const po = value || {};

  const set = (patch) => onChange({ ...po, ...patch });

  const setPulse = (key, checked) => {
    set({
      pulses: { ...(po.pulses || {}), [key]: Boolean(checked) },
    });
  };

  const setSensitivity = (key, text) => {
    set({
      sensitivity: { ...(po.sensitivity || {}), [key]: text },
    });
  };

  const setAlteration = (zone, entry) => {
    set({
      alterations: { ...(po.alterations || {}), [zone]: entry },
    });
  };

  return (
    <div className="space-y-5 rounded-xl border border-border/80 p-4 bg-muted/10">
      <h3 className="text-base font-semibold text-foreground">Exploración vascular</h3>

      <div>
        <SectionTitle>Pulsos</SectionTitle>
        <table className="w-full text-sm mt-2 border rounded-lg overflow-hidden">
          <thead className="bg-muted/40">
            <tr>
              <th className="p-2 text-left font-medium">Hallazgo</th>
              <th className="p-2 text-center w-[28%]">Izquierdo</th>
              <th className="p-2 text-center w-[28%]">Derecho</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t">
              <td className="p-2">Pulso pedio</td>
              <td className="p-2 text-center">
                <label className="inline-flex items-center gap-2">
                  <Checkbox
                    disabled={readOnly}
                    checked={!!po.pulses?.pedio_left}
                    onCheckedChange={(c) => setPulse('pedio_left', c)}
                  />
                  <span className="text-xs">Presente</span>
                </label>
              </td>
              <td className="p-2 text-center">
                <label className="inline-flex items-center gap-2">
                  <Checkbox
                    disabled={readOnly}
                    checked={!!po.pulses?.pedio_right}
                    onCheckedChange={(c) => setPulse('pedio_right', c)}
                  />
                  <span className="text-xs">Presente</span>
                </label>
              </td>
            </tr>
            <tr className="border-t">
              <td className="p-2">Pulso tibial posterior</td>
              <td className="p-2 text-center">
                <label className="inline-flex items-center gap-2">
                  <Checkbox
                    disabled={readOnly}
                    checked={!!po.pulses?.tibial_post_left}
                    onCheckedChange={(c) => setPulse('tibial_post_left', c)}
                  />
                  <span className="text-xs">Presente</span>
                </label>
              </td>
              <td className="p-2 text-center">
                <label className="inline-flex items-center gap-2">
                  <Checkbox
                    disabled={readOnly}
                    checked={!!po.pulses?.tibial_post_right}
                    onCheckedChange={(c) => setPulse('tibial_post_right', c)}
                  />
                  <span className="text-xs">Presente</span>
                </label>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="mt-3">
          <Label>Notas</Label>
          <Textarea
            rows={3}
            disabled={readOnly}
            placeholder="Notas sobre pulsos…"
            className="mt-1 min-h-[3.75rem] text-sm"
            value={po.pulses_notes || ''}
            onChange={(e) => set({ pulses_notes: e.target.value })}
          />
        </div>
      </div>

      <div>
        <SectionTitle>Temperatura</SectionTitle>
        <div className="flex flex-wrap gap-4 mt-2">
          {TEMPERATURE_OPTIONS.map((o) => (
            <RadioOption
              key={o.value}
              name="temperature"
              value={o.value}
              current={po.temperature || ''}
              label={o.label}
              readOnly={readOnly}
              onSelect={(v) => set({ temperature: v })}
            />
          ))}
          {!readOnly && po.temperature ? (
            <button
              type="button"
              className="text-xs text-muted-foreground underline"
              onClick={() => set({ temperature: '' })}
            >
              Limpiar
            </button>
          ) : null}
        </div>
      </div>

      <div>
        <SectionTitle>Llenado capilar</SectionTitle>
        <div className="flex items-center gap-2 mt-2">
          <Input
            type="number"
            min={0}
            max={30}
            disabled={readOnly}
            className="h-11 w-24"
            value={po.capillary_refill ?? ''}
            onChange={(e) => set({ capillary_refill: e.target.value })}
          />
          <span className="text-sm text-muted-foreground">segundos</span>
        </div>
      </div>

      <div>
        <SectionTitle>Edema</SectionTitle>
        <div className="flex flex-col gap-0 mt-1">
          {EDEMA_OPTIONS.map((o) => (
            <RadioOption
              key={o.value}
              name="edema"
              value={o.value}
              current={po.edema || ''}
              label={o.label}
              readOnly={readOnly}
              onSelect={(v) => set({ edema: v })}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionTitle>Sensibilidad</SectionTitle>
        <table className="w-full text-sm mt-2 border rounded-lg overflow-hidden">
          <thead className="bg-muted/40">
            <tr>
              <th className="p-2 text-left w-[36%]">Prueba</th>
              <th className="p-2 text-left">Describe</th>
            </tr>
          </thead>
          <tbody>
            {SENSITIVITY_ROWS.map((row) => (
              <tr key={row.key} className="border-t">
                <td className="p-2 font-medium align-top">{row.label}</td>
                <td className="p-2">
                  <Textarea
                    rows={2}
                    disabled={readOnly}
                    placeholder="Describe"
                    className="min-h-[3rem] text-sm"
                    value={po.sensitivity?.[row.key] || ''}
                    onChange={(e) => setSensitivity(row.key, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <SectionTitle>Alteraciones</SectionTitle>
        <table className="w-full text-sm mt-2 border rounded-lg overflow-hidden">
          <thead className="bg-muted/40">
            <tr>
              <th className="p-2 text-left">Tipo</th>
              <th className="p-2 text-left">Describe</th>
              <th className="p-2 text-center w-[100px]">Foto</th>
            </tr>
          </thead>
          <tbody>
            {ALTERATION_ZONES.map((z) => (
              <AlterationRowWithPhoto
                key={z.key}
                label={z.label}
                zone={z.key}
                value={po.alterations?.[z.key]}
                recordId={recordId}
                readOnly={readOnly}
                onChange={(entry) => setAlteration(z.key, entry)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <SectionTitle>Marcha</SectionTitle>
        <div className="flex flex-col gap-0 mt-1">
          {GAIT_TYPE_OPTIONS.map((o) => (
            <RadioOption
              key={o.value}
              name="gait_type"
              value={o.value}
              current={po.gait_type || ''}
              label={o.label}
              readOnly={readOnly}
              onSelect={(v) => set({ gait_type: v })}
            />
          ))}
        </div>
        <div className="mt-3">
          <Label>Dispositivos de apoyo (opcional)</Label>
          <Input
            className="mt-1 h-11"
            disabled={readOnly}
            value={po.gait?.devices || ''}
            onChange={(e) =>
              set({
                gait: { ...(po.gait || {}), devices: e.target.value },
              })
            }
          />
        </div>
      </div>

      <div>
        <SectionTitle>Huella plantar</SectionTitle>
        <div className="flex flex-col gap-0 mt-1">
          {PLANTAR_PRINT_OPTIONS.map((o) => (
            <RadioOption
              key={o.value}
              name="plantar_print"
              value={o.value}
              current={po.plantar_print || ''}
              label={o.label}
              readOnly={readOnly}
              onSelect={(v) => set({ plantar_print: v })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
